import { useState } from 'react';
import { Button } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import styles from './TestAutomation.module.css';
import { useTheme } from '../../hooks/useTheme';
import { useLoader } from '../../hooks/useLoader';
import TestExecutionDialog from './TestExecutionDialog';
import TestExecutionTable from './TestExecution/TestExecutionTable';
import TestExecutionSummary from './TestExecutionSummary/TestExecutionSummary';
import { testExecutionService } from '../../api/testExecutionService';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useParams } from 'react-router-dom';

const TestAutomation = () => {
  const { theme } = useTheme();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const params = useParams<{ status: string }>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showTestExecutionListTile, setShowTestExecutionListTile] = useState(false);
  const [selectedTestExecutionId, setSelectedTestExecutionId] = useState<string | null>(null);
  const [refreshTable, setRefreshTable] = useState(0);

  const handleTestExecutionClick = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleApply = async (data: {
    testCollectionId: string;
    testCollectionName: string;
    workflowId: string;
    workflowDbId: string;
    autoFlowDbId: string;
    testServiceId: string;
    rAppId: string;
    productLine: string;
    username: string;
  }) => {
    showLoader();
    try {
      console.log('Test execution data:', data);
      const apiData = {
        testCollectionName: data.testCollectionName,
        testCollectionId: data.testCollectionId,
        workflowId: data.workflowId,
        workflowDbId: data.workflowDbId,
        autoFlowDbId: data.autoFlowDbId,
        testServiceId: data.testServiceId,
        rAppId: data.rAppId,
        productLine: data.productLine,
        username: data.username
      };
      await testExecutionService.createTestExecution(apiData);
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `Test Execution created Successfully.`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      setRefreshTable((prev) => prev + 1);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch test executions. Please try again.';
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: errorMessage,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setDialogOpen(false);
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  const handleTestExecutionIdClick = (id: string) => {
    // If clicking the same ID, toggle off the tile
    if (selectedTestExecutionId === id && showTestExecutionListTile) {
      setShowTestExecutionListTile(false);
      setSelectedTestExecutionId(null);
    } else {
      // Switch to new ID's tile
      setSelectedTestExecutionId(id);
      setShowTestExecutionListTile(true);
    }
  };

  return (
    <>
      <div className={`${theme ? 'light-theme' : 'dark-theme'} ${styles.wrapper}`}>
        <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
          <div className={styles.title}>Test Execution</div>
          <div className={styles.description}>
            Manage your test executions below. Create a new one using Test Execution button, or import and delete test collections from the
            Test Collection Library.
          </div>
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
            <ReactEDSWrapper component={Button} primary onClick={handleTestExecutionClick}>
              <span className="icon icon-plus" />
              {''}
              Test Execution
            </ReactEDSWrapper>
          </div>
          <div className={styles.tabs}>
            <TestExecutionTable
              key={refreshTable}
              onTestExecutionIdClick={handleTestExecutionIdClick}
              onApply={handleApply}
              status={params.status}
            />
          </div>
        </div>

        {showTestExecutionListTile && <TestExecutionSummary testExecutionId={selectedTestExecutionId} />}
      </div>
      <TestExecutionDialog open={dialogOpen} onClose={handleDialogClose} onApply={handleApply} />
    </>
  );
};

export default TestAutomation;
